'use client'

/**
 * Purpose: Encode one still frame from a live video element.
 * Inputs: HTMLVideoElement ref target; optional ImageBase64Options (maxWidth, crop).
 * Outputs: captureFrame async function returning raw JPEG base64 or null.
 */

import { useCallback } from 'react'
import { videoFrameToBase64, type ImageBase64Options } from '@lib/image-base64'

export function useVideoFrameCapture(options: ImageBase64Options = {}) {
  const { maxWidth, jpegQuality, aspectRatio } = options

  // Primitive deps keep captureFrame stable when the caller passes a fresh options object.
  const captureFrame = useCallback(
    async (video: HTMLVideoElement | null) => {
      if (!video || !video.videoWidth) {
        return null
      }
      return videoFrameToBase64(video, { maxWidth, jpegQuality, aspectRatio })
    },
    [aspectRatio, jpegQuality, maxWidth],
  )

  return { captureFrame }
}
